import { Attributes } from "@singularsystems/neo-core"
import { Views } from '@singularsystems/neo-react';
import { AppService, Types } from '../../../TransactionsTypes';
import PortfolioVM from "../PortfolioVM";
import FutureAmounts from "../../../Models/FutureAmounts";
import AppDataLookup from "../../../Models/Portfolio/AppDataLookup";
import CalculationTranche from "../../../Models/Portfolio/Calculation/CalculationTranche";
import { PortfolioYearBucket } from "../../../Models/Portfolio/IncentiveGroup";

const payoutRatioStorageKey = "participant.payoutRatioTrackingUnits";

export default class PortfolioSummaryVM extends Views.ViewModelBase {

    constructor(
        taskRunner = AppService.get(Types.Neo.TaskRunner),
        public portfolioVM: PortfolioVM,
        private notifications = AppService.get(Types.Neo.UI.GlobalNotifications)) {

        super(taskRunner);
        this.makeObservable();
    }

    public tranches: TrancheValue[] = [];
    public tranchesById: { [index: number]: TrancheValue } = {};

    public showProfitAfterTax: boolean = false;

    @Attributes.OnChanged<PortfolioSummaryVM>(c => c.onPayoutRatioTrackingUnitsChanged)
    public payoutRatioTrackingUnits: boolean = false;

    private onPayoutRatioTrackingUnitsChanged() {
        window.localStorage.setItem(payoutRatioStorageKey, this.payoutRatioTrackingUnits.toString());
    }

    @Attributes.Float(2)
    public taxRate: number = -1;

    public setup(portfolioData: AppDataLookup) {

        const savedPreference = window.localStorage.getItem(payoutRatioStorageKey);
        if (savedPreference !== null) {
            this.payoutRatioTrackingUnits = savedPreference === "true";
        }

        this.tranches = this.portfolioVM.incentiveGroups.flatMap(
            group => group.currentTrancheBalances.map(calcTranche => new TrancheValue(calcTranche)));

        this.tranchesById = Object.fromEntries(this.tranches.map(t => [t.balance.trancheId, t]));

        if (this.taxRate === -1) {
            this.taxRate = portfolioData.settings.taxPercentageOrDefault;
        }
    }

    public get portfolioService() {
        return this.portfolioVM.portfolioService;
    }

    public get portfolioData() {
        return this.portfolioService.appData;
    }

    public get includeAllAwards() {
        const nothingSelected = this.portfolioVM.incentiveGroups.every(g => g.groupedList.every(a => !a.isSelected));
        const allSelected = this.portfolioVM.incentiveGroups.every(g => g.groupedList.every(a => a.isSelected));
        return allSelected || nothingSelected;
    }
    public set includeAllAwards(value: boolean) {
        this.portfolioVM.incentiveGroups.forEach(group => {
            group.groupedList.forEach(award => {
                // Note: this must always set isSelected to false, since it is bound to the 'reset selection' checkbox
                // which is supposed to unselect everything.
                award.isSelected = false;
            });
        });
    }

    public get valuesByYear(): FutureAmounts | null {

        const exchangeRateService = this.portfolioService.exchangeRateService;

        if (!exchangeRateService || exchangeRateService.hasMissingExchangeRates) {
            return null;
        }

        const includeAll = this.includeAllAwards;

        const baseByGroup = this.portfolioVM.incentiveGroups.map(group => ({
            group,
            tranches: includeAll
                ? group.currentTrancheBalances
                : group.getSelectedCurrentTranches(),
        }));

        const summaryTranches: TrancheValue[] = includeAll
            ? this.tranches
            : baseByGroup
                .flatMap(b => b.tranches)
                .map(tranche => this.tranchesById[tranche.trancheId])
                .filter((t): t is TrancheValue => t !== undefined);

        const futureAmounts = this.portfolioService.calculateFutureAmounts(
            exchangeRateService.lastFetchedCurrency,
            summaryTranches,
            () => 0,
            () => 0);

        const applyPayout = this.payoutRatioTrackingUnits;
        const taxFactor = this.showProfitAfterTax ? (1 - this.taxRate) : 1;

        const bucketValues = (bucket: PortfolioYearBucket) => {
            const totals = baseByGroup.reduce((acc, b) => {
                const pair = b.group.bucketProfitPair(b.tranches, bucket, futureAmounts.year1Boundary, futureAmounts.year2Boundary, applyPayout);
                acc.current += pair.current;
                acc.projected += pair.projected;
                return acc;
            }, { current: 0, projected: 0 });
            return { current: totals.current * taxFactor, projected: totals.projected * taxFactor };
        };

        const vested = bucketValues(PortfolioYearBucket.Vested);
        const yearPlus1 = bucketValues(PortfolioYearBucket.Year1);
        const yearPlus2 = bucketValues(PortfolioYearBucket.Year2);
        const yearPlus3 = bucketValues(PortfolioYearBucket.Year3Plus);
        const allTime = bucketValues(PortfolioYearBucket.Total);

        futureAmounts.vestedValue = vested.current;
        futureAmounts.vestedValue2 = vested.projected;
        futureAmounts.yearPlus1Value = yearPlus1.current;
        futureAmounts.yearPlus1Value2 = yearPlus1.projected;
        futureAmounts.yearPlus2Value = yearPlus2.current;
        futureAmounts.yearPlus2Value2 = yearPlus2.projected;
        futureAmounts.yearPlus3Value = yearPlus3.current;
        futureAmounts.yearPlus3Value2 = yearPlus3.projected;
        futureAmounts.allTimeValue = allTime.current;
        futureAmounts.allTimeValue2 = allTime.projected;

        const growth = (current: number, gross: number) => gross === 0 ? 0 : ((current / gross) - 1) * 100;
        futureAmounts.vestedGrowthPercentage = growth(futureAmounts.vestedValue, futureAmounts.grossValueAtAwardDateVestedValue);
        futureAmounts.yearPlus1GrowthPercentage = growth(futureAmounts.yearPlus1Value, futureAmounts.grossValueAtAwardDateYearPlus1Value);
        futureAmounts.yearPlus2GrowthPercentage = growth(futureAmounts.yearPlus2Value, futureAmounts.grossValueAtAwardDateYearPlus2Value);
        futureAmounts.yearPlus3GrowthPercentage = growth(futureAmounts.yearPlus3Value, futureAmounts.grossValueAtAwardDateYearPlus3Value);
        futureAmounts.allTimeGrowthPercentage = growth(futureAmounts.allTimeValue, futureAmounts.grossValueAtAwardDateAllTimeValue);

        return futureAmounts;
    }

    public get summaryCategoryLabels(): { bucket: PortfolioYearBucket; label: string }[] {
        const fa = this.valuesByYear;
        if (!fa) {
            return [];
        }
        const hasCosts = this.portfolioService.appData?.hasCostsAlreadyPaidGroups ?? false;
        const metricLabel = hasCosts ? "value" : "profit";
        const currentWording = hasCosts ? "Current potential value" : "Vested potential profit";

        return [
            { bucket: PortfolioYearBucket.Vested, label: currentWording },
            { bucket: PortfolioYearBucket.Year1, label: `Potential ${metricLabel} ${fa.yearPlus1Label}` },
            { bucket: PortfolioYearBucket.Year2, label: `Potential ${metricLabel} ${fa.yearPlus2Label}` },
            { bucket: PortfolioYearBucket.Year3Plus, label: `Potential ${metricLabel} ${fa.yearPlus3Label}` },
            { bucket: PortfolioYearBucket.Total, label: `Total potential ${metricLabel}` },
        ];
    }
}

class TrancheValue {

    constructor(
        public calc: CalculationTranche) {

        this.profitLoss = this.balance.profitLoss;
    }

    public get isVested() {
        return this.balance.isVested;
    }

    public get canTradeDate() {
        return this.balance.canTradeDate;
    }

    public get awardedUnits() {
        return this.balance.awardedUnits;
    }

    public get marketPriceAtAwardDate() {
        return this.balance.marketPriceAtAwardDate;
    }

    public awardId: number = 0;

    /** The profit / loss using the current prices, not edited prices. */
    public profitLoss: number = 0;

    public get balance() {
        return this.calc.trancheBalance;
    }

    public get instrument() {
        return this.balance.instrument;
    }
}